import store from "@/vue/planning/store/store"
import { getCookie, getParticipantsAmount, checkIfCompanyIsCgosException } from "@/utils/utils"
import { isPierreVacance } from "@/vue/planning/logic/pv"
import { transportPacks, nonTransportPacks } from "@/config/transportPackagePriorities"
import flightPackCode from "@/config/flightPackCode"
import trainPackCode from "@/config/trainPackCode"
import transportPackCode from "@/config/transportPackCode"

import availableTransportPackCodes from "@/vue/planning/computed/availableTransportPackCodes"
import { airportCodeMap } from "@/config/airportCodeMap"
import { trainStationCodeMap } from "@/config/trainStationCodeMap"
import {
    findTrainPrestIdForOrigin,
    getNativeOriginsForTrainPack,
    getPreferredTrainOrigin,
    isTrainStationOrigin,
    resolveTrainOriginForPack,
    resolveTrainPackCodeForOrigin,
} from "@/config/trainPackageConfig"


export default {
    setSelectedPrestIds: ({ state, getters, commit, dispatch }, { scrollToSummary = true, ...obj }) => {
        const prestations = getters.prestations
        const variants = getters.variants
        const selectedPrestationsAmount = getters.selectedPrestations.length
        const selectedPrestIds = state.selectedPrestIds

        let appliedSelection = false

        Object.entries(obj).forEach(([id, quantity]) => {

            /**
             * Unselects all other variants in the given prestation, and if it's a special variant,
             * it will also reset the flex type amount or DaysOfSkiPass for those unselected variants.
             * 
             * @param {Object} prestation - The prestation object containing variants.
             * @param {string|number} id - The ID of the selected variant.
             * @param {boolean} isSpecialVariant - Indicates if the variant is special.
             * @param {Object} state - The Vuex state object containing selectedFlexTypes and skiPass6or7Days.
             * @param {Function} commit - The commit function to trigger Vuex mutations.
             */

            // find prestation and variant
            let prestation = prestations.find(prest => {
                return !!prest.variants.find((v) => {
                    return v.id === id
                })
            })

            let variant = variants.find(v => v.id === id)

            const isFlexVariant = !!variant?.pvFlex
            const hasSkipass = !!variant?.skipass

            const isSpecialVariant = isFlexVariant || hasSkipass


            if (!variant) {
                return
            }

            // Handle unselection
            if (!quantity || quantity === 0) {
                // For special variants, we need to handle the cleanup
                if (isSpecialVariant) {
                    const daysAmount = `days${variant.skipass}`
                    handleSpecialVariantDecreaseAmount(variant, daysAmount, commit)
                }
                commit("setSelectedPrestIds", { [id]: 0 })
                return
            }

            // For new selections, validate
            const validationResult = validateSpecialVariatSelection(state, variant, commit);
            if (isSpecialVariant && validationResult !== true) {
                if (validationResult === 'errorMessages_7_8Days') {
                    commit("setErrorMessageOpen", "errorMessages_7_8Days");
                    return;
                } else if (validationResult === 'errorMessages_Days_Generic') {
                    commit("setErrorMessageOpen", "errorMessages_Days_Generic");
                    return;
                } else if (validationResult === 'errorMessages_PVFlex') {
                    commit("setErrorMessageOpen", "errorMessages_PVFlex");
                    return;
                } else if (isFlexVariant) {
                    commit("setErrorMessageOpen", "errorMessages_PVFlex");
                    return;
                }
                return;
            }

            const currentQuantity = selectedPrestIds[id] || 0
            const difference = quantity - currentQuantity
            const company = getCookie('company')

            prestation.variants.forEach((prestVariant) => {
                if (prestVariant.id === id) return
                const unselectObject = {}
                unselectObject[prestVariant.id] = 0
                // unselect all other variant flex type
                if (isSpecialVariant) {
                    // Unselect all other variant flex types
                    if (prestVariant.pvFlex && state.selectedFlexTypes[prestVariant.pvFlex] > 0) {
                        commit("setFlexTypeAmount", { flexType: prestVariant.pvFlex, increaseAmount: false })
                    }
                    // Reset ski pass days if selected
                    if (prestVariant.skipass && state.skiPassDays[`days${prestVariant.skipass}`] > 0) {
                        const daysAmount = `days${prestVariant.skipass}`
                        commit("setSkiPassDays", { daysAmount, increaseAmount: false })
                    }
                }
                commit("setSelectedPrestIds", unselectObject)
            })

            const remainingAdditionalHebergements = getters.remainingAdditionalHebergements

            if (difference > 0) { // if adding - check for validity

                // Enforce security : number of accommodation is limited by adult quantity
                if (state.participants.filter(age => age === 18).length <= getters.selectedPrestations.reduce((acc, prest) => acc + prest.quantity, 0)) {
                    commit("setErrorMessageOpen", "errorMessages_notEnoughAdults");
                    return;
                }

                if (store.getters.locale === "fr_FR_CSE" && checkIfCompanyIsCgosException() && selectedPrestationsAmount >= 1) {
                    commit(
                        "setErrorMessageOpen",
                        'errorMessages_morePrestationsSelectedThanOneOnCGOS'
                    )
                    return
                }
                if (store.getters.locale === "fr_FR_CSE" && checkIfCompanyIsCgosException() && prestation.capacity < getParticipantsAmount()) {
                    commit(
                        "setErrorMessageOpen",
                        'errorMessages_lessPrestationThanAdultsCGOS'
                    )
                    return
                }
                if (remainingAdditionalHebergements === 0) {
                    commit(
                        "setErrorMessageOpen",
                        'errorMessages_moreParticipantsThanCapacity'
                    )
                    return
                }
                if (remainingAdditionalHebergements - difference < 0) {
                    commit(
                        "setErrorMessageOpen",
                        'errorMessages_moreParticipantsThanCapacity'
                    )
                    return
                }
                if (quantity > variant.quantity) {
                    commit(
                        "setErrorMessageOpen",
                        'errorMessages_moreParticipantsThanCapacity'
                    )
                    return
                }
            }

            // commit if entry is valid
            const object = {}
            object[id] = quantity
            const daysAmount = `days${variant.skipass}`

            /**
             * Adjusts the amount of flex type or DaysOfSkiPass based on the quantity and 
             * updates the selected fusion ID accordingly.
             * 
             * @param {boolean} isSpecialVariant - Indicates if the variant is special (Flex || New Ski Pass Pacakge combo (PF/PFP for 6 or 7 days)).
             * @param {boolean} quantity - Indicates if a quantity exists (true to add, false to subtract).
             * @param {Object} variant - The variant object containing the details.
             * @param {number} daysAmount - The amount of days for the ski pass, after edition. (EX: '7' became 'days7')
             * @param {Function} commit - The commit function to trigger Vuex mutations.
             */
            if (isSpecialVariant && quantity) {
                handleSpecialVariantIncreaseAmount(variant, daysAmount, commit)
            } else if (isSpecialVariant && !quantity) {
                handleSpecialVariantDecreaseAmount(variant, daysAmount, commit)
            }

            commit("setSelectedPrestIds", object)
            appliedSelection = true

            // close dropdown
            commit("setSelectDropdownOpen_prestId", { id: '', current_select_location: '' })
        })

        if (appliedSelection && !state.scrollToPrestationFired && scrollToSummary) {
            setTimeout(() => {
                if (!document.querySelector("#summary")) return
                window.dispatchEvent(new CustomEvent("smoothScrollToSelector", { detail: { selector: "#summary", block: "center" } }))
            }, 200)
            commit("setScrollToPrestationFired", true)
        }

        dispatch("refreshDataStoredOnWindow")
    },
    // Handles transport selection and sets a default package accordingly
    async selectTransportWithDefaultPackage({ commit, getters, dispatch }, selected) {

        // Store the selected transportation method
        commit("setTransportationSelected", selected)
        // Keep v2 transport state in sync (used by flightSwitch/flightPackages)
        if (!selected) {
            commit("setTransportv2", null)
        }

        const available = getters.availablePackCodes

        // Exit if there are no available packages
        if (!available || !available.length) return

        // Choose the preferred package list based on whether transport is selected
        const preferredPackList = selected ? transportPacks : nonTransportPacks

        // Sort available packages by their order in the preferred package list
        const sortedAvailable = [...available].sort((a, b) => {
            return preferredPackList.indexOf(a.codePackage) - preferredPackList.indexOf(b.codePackage)
        })

        // Find the first preferred available package
        const defaultPack = sortedAvailable.find(pack =>
            preferredPackList.includes(pack.codePackage)
        )

        // If a preferred package is found, dispatch actions to set it
        if (defaultPack) {
            await dispatch("setPackage", defaultPack.codePackage)
            commit("setPackageId", defaultPack.packageId)

            if (selected) {
                if (flightPackCode.includes(defaultPack.codePackage)) {
                    commit("setTransportv2", "FLIGHT")
                } else if (trainPackCode.includes(defaultPack.codePackage)) {
                    commit("setTransportv2", "TRAIN")
                } else {
                    commit("setTransportv2", null)
                }
            }
        }

        // Set flight origin based on transport selection
        if (!selected) {
            commit("setFlightOrigin", airportCodeMap.get("None")?.name) // No transport, set to "NA"
        } else {
            dispatch("selectDefaultFlightOrigin", { overwrite: true, forceFlightPackCode: true }) // Select a default flight origin if transport is selected
        }
    },

    // Determines and sets a default flight origin based on available options
    selectDefaultFlightOrigin({ commit, getters, state, dispatch }, {overwrite, forceFlightPackCode = false}) {
        // console.log('[selectDefaultFlightOrigin]: Starting', { overwrite, forceFlightPackCode, currentFlightOrigin: state.flightOrigin, currentPackCode: state.packCode });
        if (!overwrite && state.flightOrigin && state.flightOrigin !== airportCodeMap.get("None")?.name) {
            // console.log('[selectDefaultFlightOrigin]: Flight origin already selected, exiting early', { currentFlightOrigin: state.flightOrigin });
            return; // Exit early if a flight origin is already selected
        }

        // New logic
        // console.log('[selectDefaultFlightOrigin]: wiypCall', getters.wiypCall);
        // console.log('[selectDefaultFlightOrigin]: wiypDefaultFlightOrigin', state.wiypDefaultFlightOrigin);
        if (state.wiypDefaultFlightOrigin && airportCodeMap.has(state.wiypDefaultFlightOrigin)) {
            commit('setFlightOrigin', state.wiypDefaultFlightOrigin);
            return;
        }

        const isCurrentPackageIsFlight = flightPackCode.includes(state.packCode)
        const isCurrentOriginIsFlight = airportCodeMap.has(state.flightOrigin)
        // console.log('[selectDefaultFlightOrigin]: isCurrentPackageIsFlight', isCurrentPackageIsFlight);
        // console.log('[selectDefaultFlightOrigin]: isCurrentOriginIsFlight', isCurrentOriginIsFlight);
        if(isCurrentPackageIsFlight && isCurrentOriginIsFlight){
            // console.log('[selectDefaultFlightOrigin]: is current package is flight and origin is flight, don\'t change origin');
            return;
        }

        // Get all available flight origins (e.g., ['LGW', 'MAN'])
        const availableFlightOrigins = getters.flightOrigins;

        // Filter available packages to only include those that are flight-related
        const availableFlightPackCodes = getters.availablePackCodes.filter((codeObjPackage) =>
            flightPackCode.includes(codeObjPackage.codePackage)
        );

        // console.log('[selectDefaultFlightOrigin]: availableFlightPackCodes', availableFlightPackCodes);
        // Check if the currently selected packCode is a flight pack
        const isFlightPackCode = forceFlightPackCode ? forceFlightPackCode : flightPackCode.includes(state.packCode)
        // console.log('[selectDefaultFlightOrigin]: isFlightPackCode', isFlightPackCode);
        if (availableFlightPackCodes.length && isFlightPackCode) {
            // Find the first available flight origin which has no parent in airportCodeMap
            const firstNoParentOrigin = availableFlightOrigins.find(code => {
                const info = airportCodeMap.get(code);
                return info && !info.parent;
            });

            if (firstNoParentOrigin) {
                commit('setFlightOrigin', firstNoParentOrigin);
            } else if (availableFlightOrigins.length) {
                // Fallback to the first available origin
                commit('setFlightOrigin', availableFlightOrigins[0]);
            }
        } else if (availableTransportPackCodes.value.length) {
            // If not a flight pack, fallback to the first train station code
            commit('setFlightOrigin', Array.from(trainStationCodeMap.keys())[0]);
        }
    },

    // Determines and sets a default train origin based on available options
    selectDefaultTrainOrigin({ commit, getters, state }, { overwrite, forceTrainPackCode = false, packCode, preferNative = false } = {}) {
        const noneOrigin = airportCodeMap.get("None")?.name
        const currentOrigin = state.flightOrigin?.split("-")[0] || ""
        const targetPack = packCode || state.packCode
        const isTrainPackCode = forceTrainPackCode || trainPackCode.includes(targetPack)

        if (!isTrainPackCode) {
            return
        }

        const native = getNativeOriginsForTrainPack(targetPack)
        const currentFitsNative = !!(
            currentOrigin &&
            currentOrigin !== noneOrigin &&
            native &&
            native.includes(currentOrigin)
        )

        // When switching transport (overwrite), always prefer the pack's own operator
        // origins — otherwise a leftover flight airport like AMS (also a TNE station)
        // stays paired with Eurostar PFPTT.
        const shouldPreferNative = preferNative || overwrite === true

        const origin =
            resolveTrainOriginForPack(targetPack, currentOrigin, getters.flightOrigins, { preferNative: shouldPreferNative }) ||
            getPreferredTrainOrigin(targetPack, getters.flightOrigins) ||
            getPreferredTrainOrigin(targetPack)

        if (!origin) {
            return
        }

        if (
            !overwrite &&
            isTrainStationOrigin(currentOrigin) &&
            currentOrigin !== noneOrigin &&
            origin === currentOrigin &&
            (!shouldPreferNative || currentFitsNative || !native)
        ) {
            return
        }

        commit('setFlightOrigin', origin)
    },

    // payload: packCode string, or { packCode, preferNative? }
    async setTrainPackageAndOrigin({ commit, dispatch, getters, state }, payload) {
        const packCode = typeof payload === "string" ? payload : payload?.packCode
        const preferNative = typeof payload === "object" && payload?.preferNative === true
        if (!packCode) return

        const currentOrigin = state.flightOrigin?.split("-")[0] || ""
        const origin = resolveTrainOriginForPack(
            packCode,
            currentOrigin,
            getters.flightOrigins,
            { preferNative }
        ) || getPreferredTrainOrigin(packCode, getters.flightOrigins)
        const resolvedPack = resolveTrainPackCodeForOrigin(packCode, origin)

        await dispatch("setPackage", resolvedPack)
        const availMap = state.prestMaps.seAvailabilities[resolvedPack]
        const originPrestId = findTrainPrestIdForOrigin(
            availMap ? Object.values(availMap) : [],
            resolvedPack,
            origin
        )
        const pack = getters.availablePackCodes.find(
            (availablePack) => availablePack.codePackage === resolvedPack
        )
        if (originPrestId || pack) {
            commit("setPackageId", originPrestId || pack.packageId)
        }

        if (origin && origin !== currentOrigin) {
            commit("setFlightOrigin", origin)
        }
    },

    // Sets a default package based on the origin type (flight or train)
    setDefaultPackageForOriginType: async (storeObj, flightOrigin) => {
        const { commit, dispatch, state, getters } = storeObj

        const available = getters.availablePackCodes

        // Exit early if there are no available packages
        if (!available || !available.length) return

        // Determine if the origin is a flight or train
        const isFlightOrigin = airportCodeMap.get(flightOrigin.split("-")[0])
        const isTrainOrigin = trainStationCodeMap.get(flightOrigin)

        let preferredPackList = []

        // Set preferred package list based on origin type
        if (isFlightOrigin) {
            preferredPackList = flightPackCode
        } else if (isTrainOrigin) {
            preferredPackList = transportPackCode
        } else {
            // Exit if the origin is not recognized
            return
        }

        // Sort available packages by preferred order
        const sortedAvailable = [...available].sort((a, b) => {
            return preferredPackList.indexOf(a.codePackage) - preferredPackList.indexOf(b.codePackage)
        })

        // Pick the first matching package
        const defaultPack = sortedAvailable.find(pack =>
            preferredPackList.includes(pack.codePackage)
        )

        if (defaultPack) {
            const isCurrentPackageIsTrain = trainPackCode.includes(state.packCode)
            const isCurrentOriginIsTrain = trainStationCodeMap.has(flightOrigin)
            
            const isCurrentPackageIsFlight = flightPackCode.includes(state.packCode)
            const isCurrentOriginIsFlight = airportCodeMap.has(flightOrigin)
            // Add logging to track how package/origin selection is decided
            // if( (isCurrentPackageIsFlight && isCurrentOriginIsFlight) || (isCurrentPackageIsTrain && isCurrentOriginIsTrain) ){
            //     return;
            // }
          
            // if not flight origin or train origin, change package
            const packToSelect = isTrainOrigin
                ? resolveTrainPackCodeForOrigin(defaultPack.codePackage, flightOrigin)
                : defaultPack.codePackage
            const packMatch = available.find((pack) => pack.codePackage === packToSelect) || defaultPack
            dispatch("setPackage", packMatch.codePackage)
            commit("setPackageId", packMatch.packageId)
            
            // dispatch setPackage already calls searchPrestationsForFilter
            // if (!state.firstLoad) {
            //     await searchPrestationsForFilter(storeObj); // Triggers data refresh
            // }
        }
    }

}

/**
 * Handles the increase of flex type amount or DaysOfSkiPass based on the variant type,
 * and adds the selected fusion ID.
 * 
 * @param {Object} variant - The variant object containing the details.
 * @param {number} daysAmount - The amount of days for the ski pass.
 * @param {Function} commit - The commit function to trigger Vuex mutations.
 */
function handleSpecialVariantIncreaseAmount(variant, daysAmount, commit) {
    if (variant?.pvFlex) {
        commit("setFlexTypeAmount", { flexType: variant.pvFlex })
    }
    if (variant?.prestIdFusion) {
        commit("addSelectedPrestIdFusion", variant.prestIdFusion)
    }
}

/**
 * Handles the decrease of flex type amount or DaysOfSkiPass based on the variant type,
 * and removes the selected fusion ID.
 * 
 * @param {Object} variant - The variant object containing the details.
 * @param {number} daysAmount - The amount of days for the ski pass.
 * @param {Function} commit - The commit function to trigger Vuex mutations.
 */
function handleSpecialVariantDecreaseAmount(variant, daysAmount, commit) {
    const increaseAmount = false

    if (variant?.pvFlex) {
        commit("setFlexTypeAmount", { flexType: variant.pvFlex, increaseAmount })
    }
    if (variant?.prestIdFusion) {
        commit("removeSelectedPrestIdFusion", variant.prestIdFusion)
    }
}

/**
 * Validates the selection of a special variant based on the current state.
 * It checks if the selected variant conforms to the rules regarding 
 * flex type and ski pass days selection.
 * 
 * @param {Object} state - The Vuex state object containing selectedPrestIdsFusion, selectedFlexTypes, and skiPass6or7Days.
 * @param {Object} variant - The variant object containing prestIdFusion, pvFlex, and skipass details.
 * @param {Function} commit - The commit function to trigger Vuex mutations.
 * @returns {boolean} - Returns true if the variant selection is valid based on the current state, otherwise false.
 */
function validateSpecialVariatSelection(state, variant, commit) {
    if (!variant || !state) {
        return true;
    }

    const paymentConditionOfFirstSelectedVariant = Object.keys(state.selectedPrestIds).length > 0 ? store.getters.variants.find(v => v.id === Object.keys(state.selectedPrestIds)[0]).paymentCondition : null
    if (paymentConditionOfFirstSelectedVariant !== null && paymentConditionOfFirstSelectedVariant !== variant.paymentCondition) {
        return 'errorMessages_PVFlex';
    }

    const isPrestationSelected = variant.prestIdFusion && state.selectedPrestIdsFusion &&
        state.selectedPrestIdsFusion.has(variant.prestIdFusion);

    // Check if any flex type is already selected by looking at selected variants
    const selectedVariants = Object.entries(state.selectedPrestIds)
        .filter(([_, quantity]) => quantity > 0)
        .map(([id]) => store.getters.variants.find(v => v.id === id));

    // Centralized: Forbid selecting DIFFERENT days on different prestations ONLY for non-Pierre Vacance variants
    if (!isPierreVacance(variant)) {
        const uniquePrestations = new Set(
            selectedVariants
                .filter(v => v)
                .map(v => v.prestIdFusion)
                .concat(variant.prestIdFusion)
        );

        // Only forbid if we have different prestations AND different skipass days
        if (uniquePrestations.size > 1) {
            const selectedSkipassDays = new Set(
                selectedVariants
                    .filter(v => v && v.skipass)
                    .map(v => v.skipass)
            );

            // If we already have skipass days selected and this variant has a different skipass duration
            if (selectedSkipassDays.size > 0 && variant.skipass && !selectedSkipassDays.has(variant.skipass)) {
                // Check if it's specifically 7 vs 8 days conflict
                const allDays = Array.from(selectedSkipassDays).concat(variant.skipass);
                const has7 = allDays.includes(7);
                const has8 = allDays.includes(8);
                if (has7 && has8) {
                    return 'errorMessages_7_8Days';
                }
                return 'errorMessages_Days_Generic';
            }
        }
        // For non-Pierre Vacance variants, the centralized validation above is sufficient
        return true;
    }

    // Shared variables for flex logic
    const hasSelectedFlex = selectedVariants.some(v => v && v.pvFlex);
    const selectedFlexType = hasSelectedFlex ?
        selectedVariants.find(v => v && v.pvFlex)?.pvFlex :
        null;
    const isFromSamePrestation = selectedVariants.some(v => v && v.prestIdFusion === variant.prestIdFusion);
    const selectedPrestations = new Set(selectedVariants.map(v => v && v.prestIdFusion));
    const isOnlyPrestationSelected = selectedPrestations.size === 1 && selectedPrestations.has(variant.prestIdFusion);

    // Pierre Vacance-specific logic (e.g., flex/fix restrictions)
    if (isPierreVacance(variant)) {
        // PV Flex logic: Only allow switching flex types within the same prestation
        if (isFromSamePrestation && isOnlyPrestationSelected) {
            return true;
        }

        // Check for different flex types
        if (hasSelectedFlex && selectedFlexType !== variant.pvFlex) {
            return 'errorMessages_PVFlex';
        }

        // Check for different skipass days on different prestations
        const selectedSkipassDays = new Set(
            selectedVariants
                .filter(v => v && v.skipass)
                .map(v => v.skipass)
        );

        if (selectedSkipassDays.size > 0 && variant.skipass && !selectedSkipassDays.has(variant.skipass)) {
            // Check if it's specifically 7 vs 8 days conflict
            const allDays = Array.from(selectedSkipassDays).concat(variant.skipass);
            const has7 = allDays.includes(7);
            const has8 = allDays.includes(8);
            if (has7 && has8) {
                return 'errorMessages_7_8Days';
            }
            return 'errorMessages_Days_Generic';
        }

        return true;
    }

    // For PV Flex variants - Check flex type first for ALL PV variants
    const isFlexVariant = !!variant?.pvFlex;
    if (isFlexVariant) {
        // If it's from the same prestation AND it's the only prestation selected, allow switching between flex types
        if (isFromSamePrestation && isOnlyPrestationSelected) {
            return true;
        }
        // If a flex type is selected, check if it matches the current variant's flex type
        if (hasSelectedFlex && selectedFlexType !== variant?.pvFlex) {
            return false;
        }
        return true;
    }

    // For skipass days logic (not new skipass variant logic)
    // Only allow switching days if all selected variants are from the same prestation as the one being selected
    if (
        selectedVariants.length > 0 &&
        isOnlyPrestationSelected &&
        selectedPrestations.has(variant.prestIdFusion)
    ) {
        return true;
    }
    // If no ski pass duration is selected yet, allow selection
    if (
        selectedVariants.length === 0 &&
        (!state.skiPassDays || !Object.values(state.skiPassDays).some(value => value > 0))
    ) {
        return true;
    }
    // If the variant's ski pass duration matches what's already selected, allow selection
    if (variant.skipass && state.skiPassDays[`days${variant.skipass}`]) {
        return true;
    }
    return false;
}